import {ChartType, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {renamePropertyValue} from 'common/report/util/properties';
import {API_CHART_TYPE_TO_COMMON_CHART_TYPE} from 'common/report/retention/util/constants';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {
  isErrorResponse,
  isSegmented,
  KeyedByDate,
  KeyedBySegment,
  RetentionData,
  RetentionQueryResponse,
  SegmentKeyToPath,
  SuccessResponse,
} from 'common/report/retention/models/types';
import {isRetentionTrendBasedChart, RetentionBookmarkParams, RetentionType} from 'common/report/retention/util/types';
import {QueryReason, QueryTrackingProps} from 'common/report/util/query-tracking/types';

import {getGroupByName} from './format';

export interface RequestParams {
  project_id: string;
  bookmark: RetentionBookmarkParams;
  retention_type: RetentionType;
  query_type: RetentionQueryType;
  use_query_sampling?: boolean;
  client_version: number;
  format?: string;
  tracking_props: QueryTrackingProps;
}

export type RetentionQueryType = `retention_report` | `retention_trends`;

export function getQueryType(chartType: ChartType): RetentionQueryType {
  return isRetentionTrendBasedChart(chartType) ? `retention_trends` : `retention_report`;
}

export function constructRetentionQueryParams({
  bookmarkId,
  bookmark,
  projectId,
  workspaceId,
  limitOverride,
  querySampling = false,
  hasUnsavedChanges,
  queryReason,
}: {
  bookmarkId: number;
  bookmark: RetentionBookmarkParams;
  projectId: number;
  workspaceId: number;
  limitOverride?: number;
  querySampling?: boolean;
  hasUnsavedChanges?: boolean;
  queryReason?: QueryReason;
}): RequestParams {
  bookmark.limit = limitOverride ?? REPORT_LIMITS[ReportName.Retention].query;

  const trackingProps: QueryTrackingProps = {
    is_main_query_for_report: true,
    query_reason: queryReason,
    report_name: ReportName.Retention,
    bookmark_id: bookmarkId,
    has_unsaved_changes: hasUnsavedChanges,
  };

  return {
    bookmark,
    query_type: getQueryType(API_CHART_TYPE_TO_COMMON_CHART_TYPE[bookmark.chart_type]),
    project_id: String(projectId),
    client_version: 4,
    retention_type: bookmark.retention_type,
    tracking_props: trackingProps,
    ...(workspaceId ? {workspace_id: workspaceId} : {}),
    ...(querySampling ? {use_query_sampling: querySampling} : {}),
  };
}

/**
 *
 * Transform the API response format with a multiseg tree to a tree with exactly 3 levels
 * date, flattened segments, retention/addiction data. Also returns an object for retrieving
 * the original segment path from the flattened segment string.
 **/
function flattenSegments(
  response: KeyedByDate<any>,
  segmentGroups: Segment[],
): {responseData: KeyedByDate<any>; segmentKeyToPath: SegmentKeyToPath} {
  function flatten(
    segments: KeyedBySegment<any>,
    depth: number,
    prefix = null,
    pathAccum = [],
  ): {flattened: KeyedBySegment<any>; segmentKeyToPath: SegmentKeyToPath} {
    const flattened = {};
    const segmentKeyToPath = {};
    for (const [segment, data] of Object.entries(segments)) {
      const renamedSegment = renamePropertyValue(segment, getGroupByName(segmentGroups[segmentGroups.length - depth]));
      const key = prefix ? `${prefix} / ${renamedSegment}` : renamedSegment;
      const path = [...pathAccum, renamedSegment];
      if (depth === 1) {
        flattened[key] = data;
        segmentKeyToPath[key] = path;
      } else {
        const {flattened: recursiveFlattened, segmentKeyToPath: recursivePath} = flatten(data, depth - 1, key, path);
        Object.assign(flattened, recursiveFlattened);
        Object.assign(segmentKeyToPath, recursivePath);
      }
    }

    return {flattened, segmentKeyToPath};
  }

  const responseData = {};
  let segmentKeyToPath = {};

  for (const [date, segments] of Object.entries(response)) {
    const {flattened, segmentKeyToPath: path} = flatten(segments, segmentGroups.length);
    responseData[date] = flattened;
    segmentKeyToPath = {...segmentKeyToPath, ...path};
  }

  return {responseData, segmentKeyToPath};
}

export function retentionResponseHasData(response: RetentionQueryResponse) {
  if (!response || isErrorResponse(response)) {
    return false;
  }

  const responseData = response.results || response.data;
  const comparisonData = response.comparison_results;

  return hasData(responseData) || hasData(comparisonData);

  function hasData(currData) {
    if (Array.isArray(currData)) {
      return currData?.some((num) => num > 0);
    }
    for (const key in currData) {
      if (hasData(currData[key])) {
        return true;
      }
    }
    return false;
  }
}

/**
 * Decompose and separate a RetentionQueryResponse
 * - Flatten segments if segmented (while renaming them). This applies to the main data as well as Lift and Comparison.
 * - copies metadata, comparisonMetadata unmodified
 */
export function flattenQueryResponse(
  response: RetentionQueryResponse,
  segmentGroups: Segment[],
): {
  data: RetentionData;
  compareToPastData?: RetentionData;
  liftData?: RetentionData;
  // TODO: `segmentKeyToPath` can be computed and stored directly on the rows rather than needing to be stored
  // as a separate lookup map
  // pjboudrx: not really. At the leaf-level, the "Addiction" type is a primitive,
  // so there is no place to add a "path" property without adding another level to the RetentionData structure
  // The thorough solution would be to fix the API response to send a complex type for Addiction, to match Retention
  segmentKeyToPath: SegmentKeyToPath;
  metadata?: SuccessResponse['metadata'];
  comparisonMetadata?: SuccessResponse['comparison_metadata'];
} {
  if (isErrorResponse(response)) {
    return {
      data: null,
      segmentKeyToPath: null,
    };
  } else {
    let responseData = response.results || response.data;
    let segmentKeyToPath = null;
    let compareToPastData = response.comparison_results;
    let liftData = response.lift_results;
    if (isSegmented(responseData, segmentGroups?.length > 0)) {
      ({responseData, segmentKeyToPath} = flattenSegments(responseData, segmentGroups));
      const compareToPastFlattenSegments = response.comparison_results
        ? flattenSegments(response.comparison_results, segmentGroups)
        : null;
      compareToPastData = compareToPastFlattenSegments?.responseData;
      const liftFlattenedSegments = response.lift_results
        ? flattenSegments(response.lift_results, segmentGroups)
        : null;
      liftData = liftFlattenedSegments?.responseData;
    }
    return {
      data: responseData,
      segmentKeyToPath,
      metadata: response.metadata,
      ...(response.comparison_metadata ? {comparisonMetadata: response.comparison_metadata} : {}),
      ...(compareToPastData ? {compareToPastData} : {}),
      ...(liftData ? {liftData} : {}),
    };
  }
}
